// Asistente de abastecimiento: arma la producción (OP) y la compra del horizonte.
// Pronostica el pax faltante, netea el consumo de MP contra stock + en-camino,
// agrupa la compra por proveedor con urgencia por lead time, y con gate humano
// genera las OP y crea OC borrador. Catering → Operación.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Loader2, Factory, ShoppingCart, AlertTriangle, Check, X, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, inv, num } from '@/features/inventario/shared';
import { catRpc, invalidateCatering, money } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Bodega { id: string; codigo: string; nombre: string }
interface Dia { fecha: string; pax_total: number; pax_fuente: string; tiene_orden: boolean }
interface Compra {
  material_id: string; material: string; unidad: string; necesario: number; stock: number; en_camino: number;
  neto: number; dia_primero: string; proveedor_id: string | null; proveedor: string | null;
  lead: number; dias_hasta: number; urgencia: 'urgente' | 'ok' | 'sin_proveedor'; costo_unit: number; costo_total: number;
}
interface Prov { proveedor_id: string | null; proveedor: string; lineas: number; costo_total: number; urgente: boolean }
interface Plan {
  periodo: { desde: string; hasta: string; bodega: string; dias: number };
  dias: Dia[]; compras: Compra[]; proveedores: Prov[];
  resumen: { dias: number; dias_sin_orden: number; materiales: number; costo_compra: number; urgentes: number; sin_proveedor: number };
}

const isoHoy = (): string => new Date().toISOString().slice(0, 10);
const isoMas = (n: number): string => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fechaCorta = (iso: string): string => new Date(iso + 'T12:00').toLocaleDateString(LOCALE, { weekday: 'short', day: '2-digit', month: '2-digit' });

const FUENTE: Record<string, { cls: string; label: string }> = {
  registrado: { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', label: 'registrado' },
  mixto: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', label: 'mixto' },
  estimado: { cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300', label: 'estimado' },
  sin_dato: { cls: 'bg-muted text-muted-foreground', label: 'sin dato' },
};

type Confirmar = { tipo: 'ops' } | { tipo: 'oc'; prov: Prov };

export function AbastecimientoCatering() {
  const qc = useQueryClient();
  const [desde, setDesde] = useState(isoHoy());
  const [hasta, setHasta] = useState(isoMas(6));
  const [bodegaId, setBodegaId] = useState('');
  const [confirmar, setConfirmar] = useState<Confirmar | null>(null);

  const { data: bodegas = [] } = useQuery<Bodega[]>({
    queryKey: ['cat-bodegas-abast'],
    queryFn: () => inv.list<Bodega>('locations', '?select=id,codigo,nombre&deleted_at=is.null&es_activo=eq.true&order=nombre'),
  });
  const bodega = bodegaId || bodegas.find((b) => b.codigo === 'CC01')?.id || bodegas[0]?.id || '';

  const { data: plan, isLoading, isFetching } = useQuery<Plan>({
    queryKey: ['cat-abastecimiento', bodega, desde, hasta],
    queryFn: () => catRpc<Plan>('cat_abastecimiento_plan', { _desde: desde, _hasta: hasta, _bodega: bodega }),
    enabled: !!bodega,
  });

  const genOps = useMutation({
    mutationFn: () => catRpc<{ ok: boolean; dias: { fecha: string; estado: string; detalle?: string }[] }>('cat_abastecimiento_generar_ops', { _desde: desde, _hasta: hasta, _bodega: bodega, _forecast_fill: true }),
    onSuccess: (r) => {
      invalidateCatering(qc); setConfirmar(null);
      const gen = r.dias.filter((d) => d.estado === 'generada').length;
      const err = r.dias.filter((d) => d.estado === 'error');
      toast.success(`${gen} orden(es) generada(s)${err.length ? `, ${err.length} con aviso` : ''}`);
      err.forEach((e) => toast.warning(`${e.fecha}: ${e.detalle}`));
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const comprar = useMutation({
    mutationFn: (prov: Prov) => catRpc<{ codigo: string; total: number }>('cat_abastecimiento_comprar', { _desde: desde, _hasta: hasta, _bodega: bodega, _proveedor_id: prov.proveedor_id }),
    onSuccess: (r) => { invalidateCatering(qc); setConfirmar(null); toast.success(`OC borrador ${r.codigo} creada — ${money(r.total, 2)}`); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const r = plan?.resumen;

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4" /> Asistente de abastecimiento</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div>
              <label className="block text-xs text-muted-foreground">Bodega de MP</label>
              <select className={`${fieldCls} w-56`} value={bodega} onChange={(e) => setBodegaId(e.target.value)}>
                {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </div>
            <div><label className="block text-xs text-muted-foreground">Desde</label><input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Hasta</label><input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
            {isFetching && <Loader2 className="mb-2 h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="py-6 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Analizando el horizonte…</p>}
          {!isLoading && r && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
              <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">Días</p><p className="text-xl font-semibold">{num(r.dias, 0)}</p></div>
              <div className={`rounded-md border p-2 ${r.dias_sin_orden > 0 ? 'border-sky-400' : ''}`}><p className="text-[11px] uppercase text-muted-foreground">Sin orden</p><p className="text-xl font-semibold">{num(r.dias_sin_orden, 0)}</p></div>
              <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">A comprar</p><p className="text-xl font-semibold">{num(r.materiales, 0)}</p><p className="text-[11px] text-muted-foreground">materiales</p></div>
              <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">Costo compra</p><p className="text-xl font-semibold">{money(r.costo_compra, 0)}</p></div>
              <div className={`rounded-md border p-2 ${r.urgentes > 0 ? 'border-red-400' : ''}`}><p className="text-[11px] uppercase text-muted-foreground">Urgentes</p><p className={`text-xl font-semibold ${r.urgentes > 0 ? 'text-red-600' : ''}`}>{num(r.urgentes, 0)}</p><p className="text-[11px] text-muted-foreground">lead &gt; plazo</p></div>
              <div className={`rounded-md border p-2 ${r.sin_proveedor > 0 ? 'border-amber-400' : ''}`}><p className="text-[11px] uppercase text-muted-foreground">Sin proveedor</p><p className={`text-xl font-semibold ${r.sin_proveedor > 0 ? 'text-amber-600' : ''}`}>{num(r.sin_proveedor, 0)}</p></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Producción del horizonte */}
      {plan && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm"><Factory className="h-4 w-4" /> Producción del horizonte</CardTitle>
            {plan.resumen.dias_sin_orden > 0 && (
              <div className="pt-1">
                <Button size="sm" className="gap-1" disabled={genOps.isPending} onClick={() => setConfirmar({ tipo: 'ops' })}>
                  <Factory className="h-4 w-4" /> Generar OP de {plan.resumen.dias_sin_orden} día(s) sin orden
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Fecha</th><th className="py-1 pr-2 text-right">Pax</th><th className="py-1 pr-2">Origen pax</th><th className="py-1 pr-2">Orden</th></tr></thead>
                <tbody>
                  {plan.dias.map((d) => (
                    <tr key={d.fecha} className="border-b last:border-0">
                      <td className="py-1.5 pr-2">{fechaCorta(d.fecha)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{num(d.pax_total, 0)}</td>
                      <td className="py-1.5 pr-2"><span className={`rounded-full px-2 py-0.5 text-[11px] ${FUENTE[d.pax_fuente]?.cls ?? ''}`}>{FUENTE[d.pax_fuente]?.label ?? d.pax_fuente}</span></td>
                      <td className="py-1.5 pr-2">{d.tiene_orden ? <span className="flex items-center gap-1 text-emerald-600"><Check className="h-3.5 w-3.5" /> generada</span> : <span className="text-muted-foreground">pendiente</span>}</td>
                    </tr>
                  ))}
                  {!plan.dias.length && <tr><td colSpan={4} className="py-3 text-sm text-muted-foreground">Sin producción planificada (registra pax o asigna ciclo a los contratos).</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="pt-2 text-[11px] text-muted-foreground">Pax <b>estimado</b> = mismo día de semana más reciente. Al generar, se rellenan los efectivos estimados que falten y se crea la OP de cada día (respeta excepciones del calendario).</p>
          </CardContent>
        </Card>
      )}

      {/* Compra */}
      {plan && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><ShoppingCart className="h-4 w-4" /> Compra del horizonte (neteada contra stock)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {/* Proveedores → crear OC */}
            {!!plan.proveedores.length && (
              <div className="flex flex-wrap gap-2">
                {plan.proveedores.map((pv) => (
                  <div key={pv.proveedor_id ?? 'sin'} className={`flex items-center gap-2 rounded-md border p-2 ${pv.urgente ? 'border-red-400' : ''}`}>
                    <div className="text-sm">
                      <p className="font-medium">{pv.proveedor}</p>
                      <p className="text-[11px] text-muted-foreground">{pv.lineas} línea(s) · {money(pv.costo_total, 2)}{pv.urgente && <span className="ml-1 text-red-600">· urgente</span>}</p>
                    </div>
                    {pv.proveedor_id
                      ? <Button size="sm" variant="outline" className="gap-1" disabled={comprar.isPending} onClick={() => setConfirmar({ tipo: 'oc', prov: pv })}><ShoppingCart className="h-4 w-4" /> Crear OC</Button>
                      : <span className="flex items-center gap-1 text-[11px] text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> sin proveedor</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-2">Material</th><th className="py-1 pr-2 text-right">Necesario</th><th className="py-1 pr-2 text-right">Stock</th>
                  <th className="py-1 pr-2 text-right">En camino</th><th className="py-1 pr-2 text-right">A comprar</th><th className="py-1 pr-2">Proveedor</th>
                  <th className="py-1 pr-2 text-right">Lead</th><th className="py-1 pr-2 text-right">Costo</th>
                </tr></thead>
                <tbody>
                  {plan.compras.map((c) => (
                    <tr key={c.material_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-2 font-medium">{c.material}<span className="block text-[11px] text-muted-foreground">{c.unidad}</span></td>
                      <td className="py-1.5 pr-2 text-right font-mono">{num(c.necesario, 2)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono text-muted-foreground">{num(c.stock, 2)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono text-muted-foreground">{num(c.en_camino, 2)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono font-semibold">{num(c.neto, 2)}</td>
                      <td className="py-1.5 pr-2 text-xs">{c.proveedor ?? <span className="text-amber-600">— sin proveedor —</span>}</td>
                      <td className="py-1.5 pr-2 text-right">
                        <span className={c.urgencia === 'urgente' ? 'font-semibold text-red-600' : 'text-muted-foreground'}>{c.lead}d</span>
                        {c.urgencia === 'urgente' && <span className="block text-[10px] text-red-600">llega tarde</span>}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono">{money(c.costo_total, 2)}</td>
                    </tr>
                  ))}
                  {!plan.compras.length && <tr><td colSpan={8} className="py-3 text-sm text-muted-foreground">Nada que comprar: el stock cubre la producción del horizonte.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground">A comprar = necesario − stock − en camino. Proveedor = el de la última recepción del material. <b>Urgente</b> = el lead time supera los días hasta que se necesita. La OC se crea en <b>borrador</b> (revísala y apruébala en Compras).</p>
          </CardContent>
        </Card>
      )}

      {/* Gate de confirmación */}
      {confirmar && (
        <Card className="border-primary">
          <CardContent className="flex flex-wrap items-center gap-3 py-3">
            <CalendarRange className="h-5 w-5 text-primary" />
            <div className="text-sm">
              {confirmar.tipo === 'ops'
                ? <>Generar las <b>{plan?.resumen.dias_sin_orden}</b> órdenes de producción pendientes ({fechaCorta(desde)} → {fechaCorta(hasta)}). Se rellenan los efectivos estimados que falten.</>
                : <>Crear <b>OC borrador</b> para <b>{confirmar.prov.proveedor}</b>: {confirmar.prov.lineas} línea(s), {money(confirmar.prov.costo_total, 2)}.</>}
            </div>
            <div className="ml-auto flex gap-2">
              <Button size="sm" className="gap-1" disabled={genOps.isPending || comprar.isPending}
                onClick={() => confirmar.tipo === 'ops' ? genOps.mutate() : comprar.mutate(confirmar.prov)}>
                {(genOps.isPending || comprar.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirmar
              </Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setConfirmar(null)}><X className="h-4 w-4" /> Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
